import { Component, Input } from '@angular/core';
import { PrecoAlertaDestaque } from './dashboard.service';

/** Vantagem comercial da alternativa em relação ao preço atual. */
export type VantagemAlternativa = 'vantajosa' | 'igual' | 'desvantajosa';

/** Faixa de proximidade do posto alternativo (km). */
export type FaixaProximidade = 'perto' | 'medio' | 'longe';

/** Volume de referência (litros) para estimar a economia em um tanque. */
const VOLUME_TANQUE_REFERENCIA_L = 300;

@Component({
  selector: 'app-preco-alerta-card',
  standalone: false,
  templateUrl: './preco-alerta-card.component.html',
  styleUrls: ['./preco-alerta-card.component.scss'],
})
export class PrecoAlertaCardComponent {
  @Input() alerta: PrecoAlertaDestaque | undefined;

  get badgeLabel(): string {
    const subiu = this.alerta?.direcao === 'subiu';
    const sufixoJanela = this.alerta?.janela === '30d' ? ' EM 30D' : '';
    const acao = subiu ? 'PREÇO SUBIU' : 'PREÇO DESCEU';
    const variacao = this.formatarVariacao(subiu);
    return `${acao}${sufixoJanela}${variacao}`;
  }

  get trendIcon(): string {
    return this.alerta?.direcao === 'subiu' ? 'trending_up' : 'trending_down';
  }

  /** `true` quando há `precoAnterior` para renderizar a transição "antes → depois". */
  get temPrecoAnterior(): boolean {
    return this.alerta?.precoAnterior !== undefined;
  }

  /** `true` quando o alerta possui dados de posto mais perto/barato. */
  get temMelhorAlternativa(): boolean {
    return this.alerta?.melhorAlternativa !== undefined;
  }

  /** Título do posto alternativo no padrão `00206 - POSTO 7 CHEROKEE`. */
  get tituloAlternativa(): string {
    const alt = this.alerta?.melhorAlternativa;
    if (!alt) {
      return '';
    }
    const codigo = String(alt.codPosto).padStart(5, '0');
    return `${codigo} - ${alt.fantasia}`;
  }

  /**
   * Diferença absoluta de preço por litro entre o preço atual e o da
   * alternativa. Positiva quando a alternativa é mais barata.
   */
  get economiaPorLitro(): number | null {
    const atual = this.alerta?.precoAtual;
    const alt = this.alerta?.melhorAlternativa?.preco;
    if (atual === undefined || alt === undefined) {
      return null;
    }
    return atual - alt;
  }

  /**
   * Economia percentual em relação ao preço atual. Positiva quando a
   * alternativa é mais barata.
   */
  get economiaPercentual(): number | null {
    const atual = this.alerta?.precoAtual;
    const econ = this.economiaPorLitro;
    if (atual === undefined || econ === null || atual <= 0) {
      return null;
    }
    return (econ / atual) * 100;
  }

  /**
   * Estimativa de economia para um tanque de referência (300 L). Útil
   * para tornar concreto o impacto financeiro do desvio.
   */
  get economiaPorTanque(): number | null {
    const econ = this.economiaPorLitro;
    if (econ === null) {
      return null;
    }
    return econ * VOLUME_TANQUE_REFERENCIA_L;
  }

  /** Volume de referência usado em `economiaPorTanque` (para o template). */
  get volumeReferencia(): number {
    return VOLUME_TANQUE_REFERENCIA_L;
  }

  /**
   * Classifica a alternativa como vantajosa (mais barata), igual ou
   * desvantajosa. Usado para alternar a paleta do painel sem depender
   * só de cor (vide `iconeVantagem`).
   */
  get vantagemAlternativa(): VantagemAlternativa {
    const econ = this.economiaPorLitro;
    if (econ === null || Math.abs(econ) < 0.005) {
      return 'igual';
    }
    return econ > 0 ? 'vantajosa' : 'desvantajosa';
  }

  /** Ícone Material que reforça a vantagem (não depende só de cor). */
  get iconeVantagem(): string {
    switch (this.vantagemAlternativa) {
      case 'vantajosa':
        return 'savings';
      case 'desvantajosa':
        return 'warning';
      default:
        return 'horizontal_rule';
    }
  }

  /** Rótulo curto para o selo de vantagem. */
  get rotuloVantagem(): string {
    switch (this.vantagemAlternativa) {
      case 'vantajosa':
        return 'Você economiza';
      case 'desvantajosa':
        return 'Mais cara';
      default:
        return 'Mesmo preço';
    }
  }

  /**
   * Faixa de proximidade do posto alternativo. Limites baseados no uso
   * típico de frota: até 10 km é "pertinho", até 30 km é razoável,
   * acima disso compensa avaliar.
   */
  get faixaProximidade(): FaixaProximidade {
    const km = this.alerta?.melhorAlternativa?.distanciaKm ?? Infinity;
    if (km <= 10) {
      return 'perto';
    }
    if (km <= 30) {
      return 'medio';
    }
    return 'longe';
  }

  /** Rótulo da faixa de proximidade. */
  get rotuloProximidade(): string {
    switch (this.faixaProximidade) {
      case 'perto':
        return 'Pertinho';
      case 'medio':
        return 'Próximo';
      default:
        return 'Distante';
    }
  }

  /** Descrição completa para `aria-label` do painel da alternativa. */
  get ariaAlternativa(): string {
    const alt = this.alerta?.melhorAlternativa;
    if (!alt) {
      return '';
    }
    const preco = this.formatarReais(alt.preco);
    const economia = this.economiaPorLitro;
    const tanque = this.economiaPorTanque;
    if (economia !== null && economia > 0 && tanque !== null) {
      return (
        `Melhor alternativa: ${alt.fantasia} a ${alt.distanciaKm} km por ` +
        `R$ ${preco} por litro. Economia de R$ ${this.formatarReais(economia)} por litro, ` +
        `cerca de R$ ${this.formatarReais(tanque)} em um tanque de ${VOLUME_TANQUE_REFERENCIA_L} litros.`
      );
    }
    if (economia !== null && economia < 0) {
      return (
        `Atenção: ${alt.fantasia} a ${alt.distanciaKm} km está R$ ` +
        `${this.formatarReais(Math.abs(economia))} mais caro por litro do que o preço atual.`
      );
    }
    return `Alternativa: ${alt.fantasia} a ${alt.distanciaKm} km por R$ ${preco} por litro.`;
  }

  private formatarReais(valor: number): string {
    return Math.abs(valor).toFixed(2).replace('.', ',');
  }

  private formatarVariacao(subiu: boolean): string {
    const pct = this.alerta?.percentual;
    if (pct === null || pct === undefined) {
      return '';
    }
    const n = Math.round(pct);
    return subiu ? ` (+${n}%)` : ` (−${n}%)`;
  }
}
